import {
	Box,
	Button,
	Container,
	Heading,
	Icons,
	Inline,
	Money,
	Spinner,
	Stack,
	Table,
	Text,
	Time,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	SelectInputField,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { useState } from "react"
import { Helmet } from "react-helmet-async"
import { useOutletContext } from "react-router"
import {
	ForbidUnlessAuthorized,
	PERMISSIONS,
	useCheckPermissions,
} from "../../../../../Auth"
import useSWR from "swr"
import * as Validator from "yup"
import type { ITenant } from "../../../../../Admin/Tenants"
import {
	WalletBalanceCard,
	WalletCreditsXHR,
	useAdminWalletStats,
	useWalletCreditCreateData,
	type TWalletCreditRecharge,
} from "../../../../../SubscriptionWalletCredits"

const CONSUMER_LABELS: Record<string, string> = {
	mail_message_parsings: "Emails",
}

function labelForConsumer(type: string): string {
	return CONSUMER_LABELS[type] ?? type
}

type TAddCreditsValues = {
	amount: string
	currency: string
	credits: string
	note: string
}

export default function TenantSubscriptionWalletPage() {
	const { tenant } = useOutletContext<{
		tenant: ITenant
		refresh: () => void
	}>()
	return (
		<ForbidUnlessAuthorized permission={PERMISSIONS.MANAGE_TENANTS}>
			<Helmet>
				<title>Tenant Subscription Wallet</title>
			</Helmet>
			<AdminWalletPanel tenantId={tenant.id} />
		</ForbidUnlessAuthorized>
	)
}

function AdminWalletPanel({ tenantId }: { tenantId: number }) {
	const xhr = useXHR()
	const [page, setPage] = useState(1)
	const { hasPermission } = useCheckPermissions()
	const canAddCredits = hasPermission(PERMISSIONS.MANAGE_TENANTS)
	const { data: stats, mutate: mutateStats } = useAdminWalletStats(tenantId)
	const { data: recharges, mutate: mutateRecharges } = useSWR(
		["wallet-credit-recharges", tenantId, page],
		() => WalletCreditsXHR(xhr).listRecharges(tenantId, { page, limit: 20 })
	)

	const refresh = () => {
		mutateStats()
		mutateRecharges()
	}

	return (
		<Container paddingY="4">
			<Stack gap="6">
				<Inline justifyContent="between" alignItems="center" gap="4">
					<Heading as="h4" fontSize="xl">
						Subscription Wallet
					</Heading>
					{canAddCredits ? (
						<AddCreditsDialog tenantId={tenantId} onCreated={refresh} />
					) : null}
				</Inline>

				<Inline
					gap="6"
					flexWrap="wrap"
					paddingX="4"
					paddingY="4"
					bgColor="default"
					rounded="md"
					borderWidth="1"
				>
					<WalletBalanceCard wallet={stats} />
					{stats && Object.keys(stats.consumers).length > 0 ? (
						<Stack gap="1" fontWeight="semibold">
							<Text>Usage</Text>
							{Object.entries(stats.consumers).map(([type, info], index) => (
								<Inline key={index}>
									<Text>{labelForConsumer(type)}</Text>
									&nbsp;
									<Text as="span" fontSize="xl" fontWeight="semibold">
										{info.count}
									</Text>
								</Inline>
							))}
						</Stack>
					) : null}
					{stats && stats.parser_costs.length > 0 ? (
						<Stack gap="1">
							<Text fontWeight="semibold">Supplier Cost</Text>
							{stats.parser_costs.map((row) => (
								<Inline key={row.currency}>
									<Text>{row.currency}</Text>
									&nbsp;
									<Text as="span" fontSize="xl" fontWeight="semibold">
										{Number(row.total).toFixed(6)}
									</Text>
								</Inline>
							))}
						</Stack>
					) : null}
				</Inline>

				<Stack gap="3">
					<Heading as="h5" fontSize="lg">
						Recharge History
					</Heading>
					{!recharges ? (
						<Spinner alignCenter />
					) : recharges.data.length === 0 ? (
						<Box bgColor="default" borderWidth="1" rounded="md" padding="4">
							<Text color="muted">No recharges yet.</Text>
						</Box>
					) : (
						<Stack gap="3">
							<Table
								headers={["Date", "Amount", "Credits", "Granted By", "Note"]}
								alignCols={{ 1: "right", 2: "right" }}
								bordered
								striped
								rows={recharges.data.map((r: TWalletCreditRecharge) => [
									r.created_at ? (
										<Time timestamp={r.created_at} dateFormat timeFormat />
									) : (
										<Text color="muted">-</Text>
									),
									<Money
										amount={r.amount}
										currency={r.currency}
										showCurrency
									/>,
									<Text fontWeight="semibold">{r.credits}</Text>,
									r.granted_by?.name || <Text color="muted">System</Text>,
									r.note || <Text color="muted">-</Text>,
								])}
							/>
							{recharges.meta && recharges.meta.last_page > 1 ? (
								<Inline gap="2" justifyContent="end">
									<Button
										disabled={page <= 1}
										onClick={() => setPage((p) => Math.max(1, p - 1))}
									>
										Previous
									</Button>
									<Text textAlign="center">
										Page {recharges.meta.current_page} of{" "}
										{recharges.meta.last_page}
									</Text>
									<Button
										disabled={page >= recharges.meta.last_page}
										onClick={() => setPage((p) => p + 1)}
									>
										Next
									</Button>
								</Inline>
							) : null}
						</Stack>
					)}
				</Stack>
			</Stack>
		</Container>
	)
}

function AddCreditsDialog({
	tenantId,
	onCreated,
}: {
	tenantId: number
	onCreated: () => void
}) {
	const [isOpen, open, close] = useDialog()
	const { data: createData } = useWalletCreditCreateData(tenantId, isOpen)

	const currencyOptions = createData
		? Object.keys(createData.default_credits_per_currency_unit)
		: []
	const creditsPresetMap = createData?.default_credits_per_currency_unit ?? {}

	return (
		<>
			<Button onClick={open}>
				<Icons.SparklesSolid /> Add credits
			</Button>
			<Dialog open={isOpen} sm onClose={close} title="Add credits">
				{!createData ? (
					<Dialog.Body>
						<Spinner alignCenter />
					</Dialog.Body>
				) : (
					<AddCreditsForm
						tenantId={tenantId}
						currencyOptions={currencyOptions}
						creditsPresetMap={creditsPresetMap}
						onClose={close}
						onCreated={() => {
							onCreated()
							close()
						}}
					/>
				)}
			</Dialog>
		</>
	)
}

function AddCreditsForm({
	tenantId,
	currencyOptions,
	creditsPresetMap,
	onClose,
	onCreated,
}: {
	tenantId: number
	currencyOptions: Array<string>
	creditsPresetMap: Record<string, number>
	onClose: () => void
	onCreated: () => void
}) {
	const xhr = useXHR()

	const initialValues: TAddCreditsValues = {
		amount: "100",
		currency: currencyOptions[0] || "INR",
		credits: "",
		note: "",
	}

	const validate = validateFormValues(
		Validator.object().shape({
			amount: Validator.number()
				.typeError("Amount must be a number.")
				.positive("Amount must be positive.")
				.required("Amount is required."),
			currency: Validator.string()
				.oneOf(currencyOptions, "Unsupported currency.")
				.required("Currency is required."),
			credits: Validator.string().test(
				"is-positive-int",
				"Credits must be a positive integer.",
				(v) => {
					if (!v) return true
					const n = Number(v)
					return Number.isInteger(n) && n > 0
				}
			),
			note: Validator.string().max(1000, "Note too long."),
		})
	)

	return (
		<Form<TAddCreditsValues>
			initialValues={initialValues}
			validate={validate}
			subscription={{ submitting: true, values: true }}
			onSubmit={withServerErrors(async (values) => {
				const credits = String(values.credits || "")?.trim()
					? Number(values.credits)
					: null
				await WalletCreditsXHR(xhr).createRecharge(tenantId, {
					amount: values.amount,
					currency: values.currency,
					credits,
					note: values.note.trim() || null,
				})
				showSnackbar("Credits added")
				onCreated()
			})}
		>
			{({ submitting, handleSubmit, values }) => {
				const presetRate = creditsPresetMap[values.currency] ?? 0
				const suggested = Math.floor(Number(values.amount) || 0) * presetRate
				return (
					<form noValidate onSubmit={handleSubmit}>
						<Dialog.Body>
							<Stack gap="4">
								<Inline gap="2">
									<SelectInputField name="currency" label="Currency">
										{currencyOptions.map((c) => (
											<option key={c} value={c}>
												{c}
											</option>
										))}
									</SelectInputField>
									<TextInputField
										name="amount"
										label="Amount"
										type="number"
										min="1"
									/>
								</Inline>
								<TextInputField
									name="credits"
									label="Credits (override)"
									type="number"
									min="1"
									step="1"
									placeholder={`Default: ${suggested}`}
									help={`Leave blank to apply the preset (${presetRate} credits per ${values.currency} 1).`}
								/>
								<TextAreaInputField
									name="note"
									label="Note"
									placeholder="Internal memo (optional)"
								/>
								<SubmissionError />
							</Stack>
						</Dialog.Body>
						<Dialog.Footer>
							<Inline gap="3">
								<Button type="submit" disabled={submitting}>
									{submitting ? "Adding..." : "Add credits"}
								</Button>
								<Button onClick={onClose} disabled={submitting}>
									Cancel
								</Button>
							</Inline>
						</Dialog.Footer>
					</form>
				)
			}}
		</Form>
	)
}
